import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { sparksAPI } from '../lib/api';
import { markSparkViewed } from '../lib/sparksViewed';
import { cn } from '../lib/utils';
import {
  X, ChevronLeft, ChevronRight, Trash2, Volume2, VolumeX, Pause, Play,
} from 'lucide-react';

/**
 * SparksViewer — Instagram-Stories-style full-screen player.
 *
 * Props:
 *   authors      — array of {user_id, author_name, author_picture, sparks[]}
 *   startIndex   — index of the author to start with
 *   currentUser  — logged-in user (used to allow delete on own sparks)
 *   onClose
 *   onDeleted    — called with spark_id after delete
 */
const IMAGE_DURATION_S = 7;        // images auto-advance after 7s
const TICK_MS = 50;                // progress bar tick rate

export default function SparksViewer({ authors, startIndex = 0, currentUser, onClose, onDeleted }) {
  const [authorIdx, setAuthorIdx] = useState(startIndex);
  const [sparkIdx, setSparkIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const startedRef = useRef(0);
  const elapsedRef = useRef(0);

  const author = authors[authorIdx];
  const spark = author?.sparks[sparkIdx];

  const myUid = currentUser ? (currentUser.user_id || currentUser.id) : null;
  const isOwner = myUid && spark && spark.user_id === myUid;

  // Total duration of current spark in seconds
  const sparkDuration = spark
    ? (spark.media_type === 'video' && spark.duration_seconds && spark.duration_seconds > 0
        ? Math.min(spark.duration_seconds, 34)
        : IMAGE_DURATION_S)
    : IMAGE_DURATION_S;

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const advance = useCallback(() => {
    stopTimer();
    if (!author) return;
    if (sparkIdx < author.sparks.length - 1) {
      setSparkIdx(sparkIdx + 1);
    } else if (authorIdx < authors.length - 1) {
      setAuthorIdx(authorIdx + 1);
      setSparkIdx(0);
    } else {
      onClose?.();
    }
  }, [author, sparkIdx, authorIdx, authors.length, onClose]);

  const goPrev = useCallback(() => {
    stopTimer();
    if (sparkIdx > 0) {
      setSparkIdx(sparkIdx - 1);
    } else if (authorIdx > 0) {
      const prevAuthor = authors[authorIdx - 1];
      setAuthorIdx(authorIdx - 1);
      setSparkIdx(prevAuthor.sparks.length - 1);
    }
  }, [sparkIdx, authorIdx, authors]);

  // Reset progress and start timer whenever the active spark changes
  useEffect(() => {
    setProgress(0);
    elapsedRef.current = 0;
    startedRef.current = Date.now();
    stopTimer();

    if (!spark) return;

    // Track view locally (works for anonymous users and instantly hides the
    // author from the tray once all their sparks are seen — Snapchat style).
    // Skip your own sparks so your own ring doesn't disappear from the tray.
    if (!myUid || spark.user_id !== myUid) {
      markSparkViewed(spark.spark_id);
    }

    // Register view on backend (best-effort, fire and forget) for cross-device
    // sync / analytics. Anonymous users are skipped — backend requires auth.
    if (currentUser) {
      sparksAPI.view(spark.spark_id).catch(() => {});
    }

    // For images, drive progress from a setInterval. For video, sync to the
    // video element's currentTime in onTimeUpdate.
    if (spark.media_type === 'image') {
      timerRef.current = setInterval(() => {
        if (paused) return;
        elapsedRef.current += TICK_MS;
        const p = elapsedRef.current / (sparkDuration * 1000);
        setProgress(Math.min(p, 1));
        if (p >= 1) {
          stopTimer();
          advance();
        }
      }, TICK_MS);
    }

    return stopTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spark?.spark_id, paused]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') advance();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, goPrev, onClose]);

  // Pause body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleDelete = async () => {
    if (!isOwner || !spark) return;
    if (!window.confirm('Delete this Spark? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await sparksAPI.remove(spark.spark_id);
      onDeleted?.(spark.spark_id);
      // Adjust local list / advance
      const remaining = author.sparks.filter(s => s.spark_id !== spark.spark_id);
      author.sparks = remaining;
      if (remaining.length === 0) {
        // No more sparks for this author → close or move on
        if (authors.length === 1) {
          onClose?.();
        } else {
          // Move to next author
          authors.splice(authorIdx, 1);
          if (authorIdx >= authors.length) {
            onClose?.();
          } else {
            setSparkIdx(0);
          }
        }
      } else if (sparkIdx >= remaining.length) {
        setSparkIdx(remaining.length - 1);
      }
    } catch (e) {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  const handleVideoTime = () => {
    if (!videoRef.current || paused) return;
    const t = videoRef.current.currentTime;
    setProgress(Math.min(t / sparkDuration, 1));
  };

  const handleVideoEnd = () => advance();

  if (!spark) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex flex-col"
      data-testid="sparks-viewer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Outer container caps width on large screens for portrait feel */}
      <div className="flex-1 w-full max-w-md mx-auto h-full flex flex-col relative">
        {/* Progress bars */}
        <div className="absolute top-3 left-3 right-3 flex gap-1 z-20" data-testid="sparks-progress">
          {author.sparks.map((_, i) => {
            const fill = i < sparkIdx ? 1 : i === sparkIdx ? progress : 0;
            return (
              <div key={i} className="flex-1 h-0.5 bg-white/25 rounded-full overflow-hidden">
                <div className="h-full bg-white" style={{ width: `${fill * 100}%`, transition: i === sparkIdx ? 'width 50ms linear' : 'none' }} />
              </div>
            );
          })}
        </div>

        {/* Header (author info + close) */}
        <div className="absolute top-7 left-3 right-3 z-20 flex items-center justify-between">
          <Link
            to={`/traders/${author.user_id}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white/30">
              {author.author_picture ? (
                <img src={author.author_picture} alt={author.author_name || ''} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs font-bold text-white bg-white/[0.08]">
                  {(author.author_name || 'T')[0].toUpperCase()}
                </div>
              )}
            </div>
            <div className="leading-tight">
              <p className="text-white text-sm font-semibold group-hover:underline">{author.author_name || 'Trader'}</p>
              <p className="text-white/60 text-[10px]">{timeAgo(spark.created_at)}</p>
            </div>
          </Link>

          <div className="flex items-center gap-1.5">
            {spark.media_type === 'video' && (
              <button
                onClick={(e) => { e.stopPropagation(); setMuted(m => !m); if (videoRef.current) videoRef.current.muted = !muted; }}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setPaused(p => !p); if (videoRef.current) { paused ? videoRef.current.play() : videoRef.current.pause(); } }}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              aria-label={paused ? 'Play' : 'Pause'}
            >
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
            {isOwner && (
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                disabled={deleting}
                className="w-9 h-9 rounded-full bg-red-500/20 hover:bg-red-500/40 text-red-200 flex items-center justify-center disabled:opacity-50"
                aria-label="Delete Spark"
                data-testid="sparks-delete-btn"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              aria-label="Close"
              data-testid="sparks-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tap zones for prev/next */}
        <div className="flex-1 relative flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <div
            className="absolute inset-y-0 left-0 w-1/3 z-10 cursor-pointer flex items-center justify-start pl-2"
            onClick={goPrev}
            data-testid="sparks-tap-prev"
          />
          <div
            className="absolute inset-y-0 right-0 w-2/3 z-10 cursor-pointer"
            onClick={advance}
            data-testid="sparks-tap-next"
          />

          {/* Media */}
          {spark.media_type === 'video' ? (
            <video
              key={spark.spark_id}
              ref={videoRef}
              src={spark.media_data}
              autoPlay
              muted={muted}
              playsInline
              onTimeUpdate={handleVideoTime}
              onEnded={handleVideoEnd}
              className="w-full max-h-full object-contain bg-black"
              style={{ aspectRatio: '9 / 16' }}
              data-testid="sparks-video"
            />
          ) : (
            <img
              key={spark.spark_id}
              src={spark.media_data}
              alt={spark.caption || 'Spark'}
              className="w-full max-h-full object-cover bg-black"
              style={{ aspectRatio: '9 / 16' }}
              data-testid="sparks-image"
            />
          )}

          {/* Hidden side arrows for desktop */}
          {(authorIdx > 0 || sparkIdx > 0) && (
            <button
              onClick={goPrev}
              className="hidden md:flex absolute -left-12 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white items-center justify-center z-20"
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {!(authorIdx === authors.length - 1 && sparkIdx === author.sparks.length - 1) && (
            <button
              onClick={advance}
              className="hidden md:flex absolute -right-12 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white items-center justify-center z-20"
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Caption */}
        {spark.caption && (
          <div className="absolute bottom-0 left-0 right-0 p-4 pb-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10 pointer-events-none">
            <p className="text-white text-sm leading-relaxed line-clamp-3">{spark.caption}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return d.toLocaleDateString();
}
